import { useState } from "react";
import { Col, Row } from "reactstrap";
import moment from "moment-timezone";
import { formatTimestamp, formats, timestamp } from "~/helpers/dateHelper";
import User from "~/model/user";

type TAgreementProps = {
  onConfirm: (consumerAffirmation: { readAndUnderstand: boolean }) => void;
  onCancel: () => void;
  orderSigner: User;
  isMobile?: boolean;
}

const Agreement = ({ onConfirm, onCancel, orderSigner, isMobile }: TAgreementProps) => {

  /********** STATE **********/

  const [consumerAffirmation, setConsumerAffirmation] = useState({
    readAndUnderstand: false,
  });

  return <div className="px-3 agreement-container">
    <Row className="justify-content-center">
      <Col md={7}>
        <h5 className={`agreement-title ${isMobile ? 'my-5' : 'mb-5'}`}>Consent to Use Electronic Signatures and Communications (ESIGN Consent)</h5>
        <div className="agreement-content">
          <p>This ESIGN Consent contains important information which you are entitled to receive
            before you agree to the electronic delivery of documents and communications. Your
            Dealer is required by law to give you certain information “in writing” - which means you
            are entitled to receive it on paper. However, with your prior consent, Mavsign, on behalf
            of your dealer, may instead provide this information to you electronically. We also need
            your general consent to use electronic records and signatures throughout our relationship
            with you. Before you can use Mavsign products and services, you must review and consent
            to the terms outlined below.
          </p>
          <p><strong>1. <span className="ps-3">Definitions</span></strong></p>
          <p>• In this ESIGN Consent:</p>
          <div className="pb-2">• “We,” “us,” “our” and “Mavsign” means Maverick Document Signings, LLC.</div>
          <div className="pb-2">• “You” and “your” means the person giving this ESIGN Consent, and also each
            additional account owner, authorized signer, authorized representative, delegate,
            product owner and/or service user identified on any Mavsign Product that you
            apply for, use, or access.
          </div>
          <div className="pb-2">• “Communications” means each disclosure, notice, agreement, fee schedule,
            statement, record, document, credit application, retail contract or lease
            agreement, notice, authorization, amendment, and other information we provide
            to you in connection with the Transaction, or that you sign, submit, or agree to at
            our request.
          </div>
          <div className="pb-2">• “Dealer” means the licensed motor vehicle dealer from which you have purchase
            order lease or intend to purchase or lease a motor vehicle.
          </div>
          <div className="pb-2">• “Mavsign Product” means each and every account, product, or service we offer that
            you apply for, own, use, administer or access, either now or in the future.   </div>
          <div className="pb-2">• “Transaction” means the sale, extension of credit, lease, or similar transaction
            between you and the Dealer.
          </div>
          <div className="pb-2">• “Include” and “Including,” when used at the beginning of a list of one or more items,
            indicates that the list contains examples; the list is not exclusive or exhaustive, the
            items in the list are only illustrations, and the items are not the only possible items
            that could appear in the list.
          </div>

          <p className="pt-4"><strong>2. <span className="ps-3">Scope of Consent</span></strong></p>
          <div>By giving your affirmative consent, you agree that we may deliver all Communications to
            you electronically. You also agree to sign Communications electronically. This ESIGN
            Consent is effective unless and until you withdraw your ESIGN Consent as described in
            Section 3, below.
          </div>

          <p className="pt-4"><strong>3. <span className="ps-3">Withdrawing Consent</span></strong></p>
          <div>In order to withdraw consent, you may email us at <a href="mailto:cs@mavsign.com">cs@mavsign.com</a> or call us at 877-708-4383. If you withdraw your consent, you will have to go to the dealership or have the
            4383. If you withdraw your consent, you will have to go to the dealership or have the
            dealer mail you paper documents for review and signature.
          </div>

          <p className="pt-4"><strong>4. <span className="ps-3">Requesting Paper Copies</span></strong></p>
          <div>You can obtain a paper copy of an electronic Communication by printing or downloading
            it or by requesting that Dealer mail you a paper copy. If allowed by applicable law, Dealer
            or we may charge you a reasonable service charge, with prior notice of any such charge,
            for the delivery of paper copies of any Communication provided to you electronically
            pursuant to ESIGN Consent. To request a paper copy, you may email us at <a href="mailto:cs@mavsign.com">cs@mavsign.com</a>
            or call us at 877-708-4383.
          </div>

          <p className="pt-4"><strong>5. <span className="ps-3">Updating Your Contact Information</span></strong></p>
          <div>You agree to provide us with your accurate personal contact information and to promptly
            notify us of any changes. You can update your contact information (including your email
            address and mobile phone number) by emailing us at <a href="mailto:cs@mavsign.com">cs@mavsign.com</a> or calling us at
            877-708-4383.
          </div>

          <p className="pt-4"><strong>6. <span className="ps-3">Hardware and Software Requirements</span></strong></p>
          <div>In order to use electronic signatures and Communications you must have access to the
            following technology:
          </div>
          <div>• A current version (defined below) of an Internet browser, such as Safari, Chrome, or Edge;</div>
          <div>• A connection to the Internet;</div>
          <div>• A current version of a program that accurately reads and displays PDF files; and</div>
          <div>• A computer and operating system capable of supporting all of the above. You will
            also need a printer if you wish to print out and retain communications on paper
            and electronic storage if you wish to retain communications in electronic form.
          </div>
          <div>By “current version,” we mean a version of the software that is currently being supported by its publisher.</div>
          <div>We reserve the right to discontinue support of a current version of software if, in our sole opinion, it suffers from a security flaw or other flaw that makes it unsuitable for use.  </div>
          <div>To verify that you have all of the necessary hardware and software for you to use
            electronic signatures and can receive electronic Communications on your devices, please
            verify that you were able to read this ESIGN Consent and that you also were able to print
            on paper or electronically save this ESIGN Consent.
          </div>

          <p className="pt-4"><strong>7. <span className="ps-3">Your Agreement to the use of Electronic Communications and Signatures</span></strong></p>
          <div>By agreeing, you confirm that: </div>
          <div>• You have read, understand, and agree to this ESIGN Consent;</div>
          <div>• The electronic signatures and Communications will have the same legal effect as written and/or signed paper documents;</div>
          <div>• You have the necessary hardware and software to view, save and print copies of Communications and to receive Communications that we send to you by email;</div>
          <div>• Your consent to receive Communications electronically does not expire and is effective unless withdrawn as described herein;  </div>
          <div>• You may change your electronic delivery settings at any time by emailing us at <a href="mailto:cs@mavsign.com">cs@mavsign.com</a> or calling us at 877-708-4383;</div>
          <div>• Communications and disclosures provided may contain important information and other legal disclosures concerning your accounts and you agree to review such Communications in a timely manner.</div>

          <p className="pt-4"><strong>8. <span className="ps-3">Retain Copies for Your Records</span></strong></p>
          <div>We recommend that you print or download a copy of this ESIGN Consent and all other Communications Dealer or we provide you for your permanent records.</div>
          <div className="pt-2 pb-5">By providing your consent, you are also confirming that you have the hardware and software described above, that you are able to receive and review electronic
            Communications, and that you have an active email account. You are also confirming
            that you are authorized to, and do, consent on behalf of all the other account owners,
            authorized signers, authorized representatives, delegates, product owners and/or
            service users identified with your Mavsign Products.
          </div>

          <div className="agreement-info">
            <div>Name: {orderSigner?.fullName}</div>
            <div>Email Address: {orderSigner?.email}</div>
            <div>Date: {formatTimestamp(timestamp(moment()), formats.US_DATE)}</div>
          </div>

          <div className="py-4">I have read and understand this Electronic Records and Signatures Disclosure and Consent.</div>
        </div>

        <div className="py-4 ps-2">
          <div className="mt-3"><b>Consumer Affirmation</b></div>
          <div className="form-check my-2">
            <input
              className="form-check-input"
              type="checkbox"
              name="readAndUnderstand"
              id="readAndUnderstand"
              value={''}
              checked={consumerAffirmation.readAndUnderstand}
              onChange={(e) => setConsumerAffirmation({ ...consumerAffirmation, readAndUnderstand: e.target.checked })}
            />
            <label className="form-check-label" htmlFor="readAndUnderstand"> I consent to receive required disclosures, agreements, and notices electronically.</label>
          </div>
        </div>
        <div className="d-flex justify-content-center my-5">
          <button onClick={() => onConfirm(consumerAffirmation)} disabled={!consumerAffirmation.readAndUnderstand} className="px-5 py-2 mx-2 btn btn-primary">I Agree</button>
          <button onClick={onCancel} className="px-5 py-2 mx-2 btn btn-secondary">Cancel</button>
        </div>
      </Col>
    </Row>
  </div>
}

export default Agreement;
